import React, { useEffect } from 'react'
import { H4, ProgressBar } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Flex, Icon, PrettyNumber } from '@ui'
import usePollImportStatus from '../hooks/usePollStatus'
import { ApolloError } from 'apollo-boost'

interface EmployeeImportProgressProps {
  file: File | null
  importId: string
  callback: (error: ApolloError | null) => void
}

export default function EmployeeImportProgress({
  file,
  importId,
  callback,
}: EmployeeImportProgressProps) {
  const { error, newStatus, progress, total, processed } = usePollImportStatus(importId)

  useEffect(() => {
    if (error || newStatus === 'completed') {
      callback(error)
    }
  }, [callback, newStatus, error])

  return (
    <>
      <div style={{ padding: '30px 60px 60px 60px' }}>
        <Flex align="flex-end" justify="space-between" style={{ marginBottom: 10 }}>
          <div>
            <H4 style={{ marginBottom: 30 }}>Importing...</H4>
            {file && (
              <Flex
                align="center"
                style={{
                  border: '1px solid var(--border-color)',
                  padding: '3px 7px',
                  borderRadius: 4,
                }}
              >
                <Icon name="file-csv" style={{ color: 'dodgerblue', marginRight: 10 }} />
                {file.name}
              </Flex>
            )}
          </div>
          {progress === null && (
            <Tooltip2 content="File is waiting to be processed">
              {newStatus === 'pending_approval' ? 'Pending Approval' : 'Pending'}
            </Tooltip2>
          )}
          {newStatus === 'queued' &&
            total !== 0 &&
            (processed === total ? (
              <span>Finalizing...</span>
            ) : (
              <span>
                <PrettyNumber value={processed} /> of <PrettyNumber value={total} /> records
                processed
              </span>
            ))}
        </Flex>
        <ProgressBar
          stripes={false}
          value={newStatus === 'completed' ? 1 : progress !== null ? progress : 0}
        />
      </div>
      <p style={{ textAlign: 'center', color: 'var(--grey)' }}>
        You can close this window anytime. Your import will continue in the background. Once the
        import is completed, you will receive a confirmation email. The import details will also
        appear in the Employee Import History.
      </p>
    </>
  )
}
